import { Button, Modal } from 'react-bootstrap';
import { EnumerateDefensores } from '@/modules/people/defensor.service';
import { Form, useParams } from 'react-router-dom';
import { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import { GetSubstituicoesPeriodos } from '@/modules/business/periodo/periodo.service';
import { GetTitularidades } from '@/modules/structure/defensoria/defensoria.service';
import { LotacaoService } from '@/modules/business/lotacoes/lotacao.service';
import { ReadDefensorResponseDto, ReadSubstituicaoDto } from '@/api.types';
import { SearchDtoLotacoes } from '@/shared/types/search.dto';
import { toast } from 'react-toastify';

interface parametros {
  updateSubstituicoes: (lista: ReadSubstituicaoDto[]) => void;
  defensorid?: number
}

export default function TabelaSubstituicoes({ updateSubstituicoes, defensorid }: parametros) {

  const params = useParams();

  const id = params.id ? Number(params.id) : 0;
  // const defensorid = params.defensorid ? Number(params.defensorid) : 0;

  // Servicos - usando useMemo para evitar recriação desnecessária
  const lotacaoService = useMemo(() => new LotacaoService(), []);

  const [defensores, setDefensores] = useState<ReadDefensorResponseDto[]>([]);
  const [defensorias, setDefensorias] = useState<ReadSubstituicaoDto[]>([]);
  const [substituicoes, setSubstituicoes] = useState<ReadSubstituicaoDto[]>([]);

  const [showModal, setShowModal] = useState(false);

  const [uiDefensoriaModal, setUiDefensoriaModal] = useState<number | undefined>();
  const [uiDefensorModal, setUiDefensorModal] = useState<number | undefined>();
  const [uiAnotacaoModal, setUiAnotacaoModal] = useState<string | undefined>();
  const [uiPontuarModal, setUiPontuarModal] = useState<boolean>(false);

  useEffect(() => {
    void Promise.all([
      EnumerateDefensores(0, 1000),
      lotacaoService.Enumerate({ skip: 0, take: 1000, id_defensor: defensorid, somente_ativas: true } as SearchDtoLotacoes),
      id > 0 ? GetSubstituicoesPeriodos(id) : Promise.resolve(null),
      GetTitularidades(),
    ])
      .then((results) => {
        setDefensores(results[0].objects.filter((a) => a.id !== defensorid));

        const substituicoesCarregadas = results[2] ? results[2].objects : [];
        setSubstituicoes(substituicoesCarregadas);

        const s = lotacaoService.MergeLotacaoSubstituicao(
          id,
          substituicoesCarregadas,
          results[1][0].filter(a => a.defensoria?.id),
          results[3].objects,
        );
        setDefensorias(s);

        // Atualizar substituições no componente pai - deferir para evitar atualização durante render
        queueMicrotask(() => {
          updateSubstituicoes(substituicoesCarregadas);
        });
      })
      .catch((error) => {
        console.error('Erro ao carregar dados:', error);
        toast.error('Erro ao carregar dados das substituições.');
      });
  }, [id, defensorid, lotacaoService, updateSubstituicoes]);

  const handleShow = useCallback((rowData: ReadSubstituicaoDto | null) => {
    if (rowData) {
      setUiDefensoriaModal(rowData.defensoria?.id);
      setUiDefensorModal(rowData.substituto?.id);
      setUiAnotacaoModal(rowData.anotacao || '');
      setUiPontuarModal(rowData.pontuar || false);
    } else {
      setUiDefensorModal(defensores[0]?.id);
      setUiAnotacaoModal('');
      setUiPontuarModal(false);

      if (defensorias.length > 0) {
        setUiDefensoriaModal(defensorias[0].defensoria?.id);
      } else {
        toast.warn(
          'O(a) defensor(a) não possui lotações, não há o que substituir.',
        );
        return;
      }
    }
    setShowModal(true);
  }, [defensores, defensorias]);

  const salvarSubstituto = useCallback(() => {
    if (!uiDefensoriaModal || !uiDefensorModal) {
      toast.warn('Selecione uma defensoria e um defensor substituto.');
      return;
    }

    setSubstituicoes((prevSubstituicoes) => {
      const jaExiste = prevSubstituicoes.findIndex(
        (item) =>
          item.defensoria?.id === uiDefensoriaModal &&
          item.substituto?.id === uiDefensorModal,
      );

      let novasSubstituicoes: ReadSubstituicaoDto[];

      if (jaExiste >= 0) {
        // Atualizar substituição existente
        novasSubstituicoes = prevSubstituicoes.map((item, index) =>
          index === jaExiste
            ? { ...item, anotacao: uiAnotacaoModal || '', pontuar: uiPontuarModal }
            : item
        );
      } else {
        // Adicionar nova substituição
        const defensoria = defensorias.find(
          (e) => e.defensoria?.id === uiDefensoriaModal,
        )?.defensoria;
        const substituto = defensores.find((e) => e.id === uiDefensorModal);

        if (!defensoria || !substituto) {
          toast.error('Erro ao encontrar defensoria ou defensor.');
          return prevSubstituicoes;
        }

        const novoObjeto: ReadSubstituicaoDto = {
          id: 0, // ID temporário para novas substituições
          anotacao: uiAnotacaoModal || '',
          pontuar: uiPontuarModal,
          defensoria,
          substituto,
        } as unknown as ReadSubstituicaoDto;

        novasSubstituicoes = [...prevSubstituicoes, novoObjeto];
      }

      // Atualizar no componente pai - deferir para evitar atualização durante render
      queueMicrotask(() => {
        updateSubstituicoes(novasSubstituicoes);
      });
      return novasSubstituicoes;
    });

    setShowModal(false);
  }, [uiDefensoriaModal, uiDefensorModal, uiAnotacaoModal, uiPontuarModal, defensorias, defensores, updateSubstituicoes]);

  const removerSubstituicao = useCallback((
    idDefensoria: number | undefined,
    idDefensor: number | undefined,
  ) => {
    setSubstituicoes((prevSubstituicoes) => {
      const novasSubstituicoes = prevSubstituicoes.filter(
        (item) =>
          !(item.defensoria?.id === idDefensoria && item.substituto?.id === idDefensor)
      );

      // Atualizar no componente pai - deferir para evitar atualização durante render
      queueMicrotask(() => {
        updateSubstituicoes(novasSubstituicoes);
      });
      return novasSubstituicoes;
    });
  }, [updateSubstituicoes]);

  return (
    <Fragment>
      <div className="card">
        <div className="card-header position-relative">
          <h5 className="text-center mb-0">Substituições</h5>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary position-absolute top-50 end-0 translate-middle-y me-3"
            onClick={() => handleShow(null)}
            title="Adicionar Substituição"
          >
            Adicionar Substituição
          </button>
        </div>

        <div className="card-body row">
          <table className="table table-sm table-bordered table-hover table-striped">
            <thead>
              <tr>
                <th>Defensoria</th>
                <th>Defensores</th>
                <th>Anotação</th>
                <th>Pontuar</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {substituicoes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    Nenhuma substituição cadastrada
                  </td>
                </tr>
              ) : (
                substituicoes.map((item, index) => (
                  <tr key={`${item.defensoria?.id}-${item.substituto?.id}-${index}`}>
                    <td>{item.defensoria?.nome}</td>
                    <td>{item.substituto?.pessoa?.nome}</td>
                    <td>{item.anotacao}</td>
                    <td>{item.pontuar ? 'Sim' : 'Não'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-outline-info btn-sm me-2"
                        onClick={() => handleShow(item)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() =>
                          removerSubstituicao(
                            item.defensoria?.id,
                            item.substituto?.id,
                          )
                        }
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        className="modal-lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Substituições</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="mb-2">
              <label htmlFor="uiDefensoriaSelecionado">Defensoria</label>
              <select
                id="uiDefensoriaSelecionado"
                title="Defensoria"
                className="form-select"
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setUiDefensoriaModal(e.target.value ? Number(e.target.value) : undefined)
                }
                value={uiDefensoriaModal || ''}
              >
                <option value="">Selecione uma defensoria</option>
                {(defensorias ?? [])
                  .filter((item, index, self) =>
                    index === self.findIndex(d => d.defensoria?.id === item.defensoria?.id)
                  )
                  .sort((a, b) => (a.defensoria?.nome ?? "").localeCompare(b.defensoria?.nome ?? ""))
                  .map(item => (
                    <option key={item.defensoria?.id} value={item.defensoria?.id}>
                      {item.defensoria?.nome}
                    </option>
                  ))}
              </select>
            </div>
            <div className="mb-2">
              <label htmlFor="uiDefensorSelecionado">
                Defensor Substituto:{' '}
              </label>
              <select
                id="uiDefensorSelecionado"
                title="Defensor"
                className="form-select"
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setUiDefensorModal(e.target.value ? Number(e.target.value) : undefined)
                }
                value={uiDefensorModal || ''}
              >
                <option value="">Selecione um defensor</option>
                {defensores.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.pessoa?.nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-2">
              <label htmlFor="uiAnotacao">Anotação: </label>
              <input
                id="uiAnotacao"
                type="text"
                title="Anotação"
                className="form-control"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setUiAnotacaoModal(e.target.value)
                }
                value={uiAnotacaoModal || ''}
              ></input>
            </div>
            <div className="mb-2">
              <div className="form-check">
                <input
                  className="form-check-input"
                  title="Se gera pontuação"
                  type="checkbox"
                  id="uiPontuar"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setUiPontuarModal(e.target.checked)
                  }
                  checked={uiPontuarModal}
                />
                <label className="form-check-label" htmlFor="uiPontuar">
                  Pontuar
                </label>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={salvarSubstituto}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
}
